import { BaseText, semantic_colors, VStack, VStackProps } from '@croquiscom/pds';
import styled from '@emotion/styled';
import { FC, MouseEvent, ReactNode } from 'react';
import { flex } from '@/common/styles';
import { TabIdProvider } from '@/components/common/PastelTab/TabIdProvider';

interface TabItem {
  id: string;
  label: string;
}

interface TabPanel {
  id: string;
  element: ReactNode;
}

interface Props extends VStackProps {
  tab_id?: string;
  items: TabItem[];
  onTabChange?: (id: string) => void;
  panels: TabPanel[];
}

export const PastelTab: FC<Props> = ({ tab_id, items, panels, onTabChange, ...rest }) => {
  const handleTabClick = (e: MouseEvent<HTMLButtonElement>) => {
    const { id } = e.currentTarget.dataset;
    id && onTabChange?.(id);
  };

  const { element } = panels.find(({ id, element }) => (id === tab_id ? element : null)) ?? {};

  return (
    <SC.Root {...rest}>
      <SC.ButtonRoot>
        {items.map(({ id, label }) => {
          const is_active = id === tab_id;
          const color = is_active ? semantic_colors.content.primary : semantic_colors.content.tertiary;
          return (
            <SC.Button key={id} data-id={id} is_active={is_active} onClick={handleTabClick}>
              <BaseText kind='Heading_15_SemiBold' color={color}>
                {label}
              </BaseText>
            </SC.Button>
          );
        })}
      </SC.ButtonRoot>
      <TabIdProvider tab_id={tab_id ?? ''}>{element}</TabIdProvider>
    </SC.Root>
  );
};

const SC = {
  Root: styled(VStack)`
    border: none;
    border-left: 1px solid ${({ theme }) => theme.palette.border.primary};
    border-right: 1px solid ${({ theme }) => theme.palette.border.primary};
    border-bottom: 1px solid ${({ theme }) => theme.palette.border.primary};
  `,
  ButtonRoot: styled.div`
    ${flex({ alignment: 'stretch' })}
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
  `,
  Button: styled.button<{ is_active?: boolean }>`
    ${flex({ alignment: 'center', justify: 'center' })}
    flex: 1;
    padding: 16px;
    cursor: pointer;
    background-color: ${({ is_active, theme }) =>
      is_active ? theme.palette.background.surface : theme.palette.background.disabled};
    border: none;
    border-top: ${({ is_active, theme }) =>
      is_active ? `2px solid ${theme.palette.content.primary}` : `1px solid ${theme.palette.border.primary}`};
    border-bottom: ${({ is_active, theme }) =>
      is_active ? `1px solid ${theme.palette.background.surface}` : `1px solid ${theme.palette.border.primary}`};
    border-right: 1px solid ${({ theme }) => theme.palette.border.primary};

    :last-of-type {
      border-right: none;
      border-top-right-radius: inherit;
      border-bottom-right-radius: inherit;
    }

    :first-of-type {
      border-top-left-radius: inherit;
      border-bottom-left-radius: inherit;
    }
  `,
};
